import { useState } from 'react';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    major: '',
    year: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleYearChange = (value) => {
    setFormData({
      ...formData,
      year: value
    });
  };

  const validateEmail = (email) => {
    const osuEmailPattern = /^[^\s@]+@(osu\.edu|buckeyemail\.osu\.edu)$/i;
    return osuEmailPattern.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate OSU email
    if (!validateEmail(formData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please use your OSU email address (@osu.edu or @buckeyemail.osu.edu)",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setIsSuccess(true);
        toast({
          title: "Success!",
          description: response.data.message,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          major: '',
          year: '',
          message: ''
        });

        setTimeout(() => setIsSuccess(false), 3000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Something went wrong. Please try again or email us directly at info@buckeyesilicon.com";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <img 
          src="https://images.unsplash.com/photo-1562408590-e32931084e23?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Background pattern"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Get Involved
          </h2>
          <div className="w-24 h-1 bg-[#BB0000] mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Ready to start your journey in chip design? Fill out the form below and we'll get back to you soon!
          </p>
        </div>

        {/* Contact Form */}
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-gray-100 p-8 sm:p-12">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name" className="text-gray-700 font-semibold mb-2 block">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="border-gray-300 focus:border-[#BB0000] focus:ring-[#BB0000]"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-700 font-semibold mb-2 block">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john.doe@osu.edu"
                  className="border-gray-300 focus:border-[#BB0000] focus:ring-[#BB0000]"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="major" className="text-gray-700 font-semibold mb-2 block">
                  Major *
                </Label>
                <Input
                  id="major"
                  name="major"
                  type="text"
                  required
                  value={formData.major}
                  onChange={handleChange}
                  placeholder="Computer Engineering"
                  className="border-gray-300 focus:border-[#BB0000] focus:ring-[#BB0000]"
                />
              </div>

              <div>
                <Label htmlFor="year" className="text-gray-700 font-semibold mb-2 block">
                  Academic Year *
                </Label>
                <Select onValueChange={handleYearChange} value={formData.year} required>
                  <SelectTrigger className="border-gray-300 focus:border-[#BB0000] focus:ring-[#BB0000]">
                    <SelectValue placeholder="Select your year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Freshman">Freshman</SelectItem>
                    <SelectItem value="Sophomore">Sophomore</SelectItem>
                    <SelectItem value="Junior">Junior</SelectItem>
                    <SelectItem value="Senior">Senior</SelectItem>
                    <SelectItem value="Graduate Student">Graduate Student</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="message" className="text-gray-700 font-semibold mb-2 block">
                Why do you want to join Buckeye Silicon? *
              </Label>
              <Textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your interest in chip design and what you hope to gain from joining our organization..."
                rows={6}
                className="border-gray-300 focus:border-[#BB0000] focus:ring-[#BB0000] resize-none"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || isSuccess}
              className="w-full bg-[#BB0000] hover:bg-[#990000] text-white py-6 text-lg font-semibold transition-all hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {(() => {
                if (isSubmitting) {
                  return (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Submitting...
                    </>
                  );
                }
                if (isSuccess) {
                  return (
                    <>
                      <CheckCircle className="mr-2" size={20} />
                      Submitted Successfully!
                    </>
                  );
                }
                return (
                  <>
                    <Send className="mr-2" size={20} />
                    Submit Application
                  </>
                );
              })()}
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-200 text-center">
            <p className="text-gray-600">
              Have questions? Email us at{' '}
              <a href="mailto:info@buckeyesilicon.com" className="text-[#BB0000] hover:underline font-semibold">
                info@buckeyesilicon.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
